// AI Studio: everything that happens while templates are generated, for the whole app.
//
// - Starts generations and follows them (the API works on them in the background, so they go on
//   when the user changes page, opens the editor, refreshes or comes back later).
// - When the AI's plan is ready, builds the scenes in this browser (Utils/aiTemplate), draws the
//   scene thumbnails and sends the finished project to the API, which saves it in My Projects.
// - Shows the progress panel and the small notifications (bottom right) on every page.
// - Admins' batches (many templates at once): built one after the other in this browser, with one
//   notification per batch instead of one per template.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import AuthContext from "../context/userAuth.context";
import { aiApi, planOf } from "../Utils/aiTemplate/aiApi";
import { buildSlides, musicFor, withCanvas, DEFAULT_WATERMARK } from "../Utils/aiTemplate/aiBuilder";
import { renderSlide } from "../Utils/aiTemplate/aiRender";
import { packProjectData } from "../Utils/projectPack";
import AIProgressPanel from "./AIProgressPanel";
import AIToasts from "./AIToasts";
import "./aiStudio.css";

const AIJobsContext = createContext(null);

const SERVER_RUNNING = ["queued", "planning"];
export const RUNNING = ["queued", "planning", "planned", "building"];
const POLL_MS = 2000;
const LOCK_MS = 90 * 1000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Two open tabs must not both build the same template.
const lockKey = (id) => "toons_ai_building_" + id;
function takeLock(id) {
  try {
    const at = Number(localStorage.getItem(lockKey(id)) || 0);
    if (at && Date.now() - at < LOCK_MS) return false;
    localStorage.setItem(lockKey(id), String(Date.now()));
  } catch (e) {
    /* storage blocked: build anyway */
  }
  return true;
}
function refreshLock(id) {
  try {
    localStorage.setItem(lockKey(id), String(Date.now()));
  } catch (e) {
    /* ignore */
  }
}
function releaseLock(id) {
  try {
    localStorage.removeItem(lockKey(id));
  } catch (e) {
    /* ignore */
  }
}
/** Another open tab is building this template right now. */
function lockedElsewhere(id) {
  try {
    const at = Number(localStorage.getItem(lockKey(id)) || 0);
    return !!at && Date.now() - at < LOCK_MS;
  } catch (e) {
    return false;
  }
}
const RETRY_MS = 15 * 1000;

const NOOP = {
  available: false,
  jobs: [],
  settings: null,
  generate: async () => ({ status: false, message: "AI Studio is not ready." }),
};

export const useAIJobs = () => useContext(AIJobsContext) || NOOP;

export const BATCH_TAB_KEY = "toons_ai_tab";

export function AIJobsProvider({ children }) {
  const { authenticationData, setAllProject, setDivName } = useContext(AuthContext);
  const history = useHistory();
  const api = authenticationData.ApiBaseURL;
  const enabled = !!authenticationData.isAuthenticated;

  const [jobsById, setJobsById] = useState({});
  const [settings, setSettings] = useState(null);
  const [available, setAvailable] = useState(true);
  const [panelId, setPanelId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const jobsRef = useRef({});
  jobsRef.current = jobsById;
  const panelRef = useRef(null);
  panelRef.current = panelId;
  const building = useRef(new Set());
  const announced = useRef(new Set());
  const buildRef = useRef(null);
  const pumpRef = useRef(null);
  const pumpTimer = useRef(null);

  // jobsRef is updated at once (not only on the next render), so code that runs right after a
  // merge (the batch queue, the "batch finished" check) sees every job.
  const merge = useCallback((job, local = {}) => {
    if (!job || job.id == null) return;
    jobsRef.current = { ...jobsRef.current, [job.id]: { ...(jobsRef.current[job.id] || {}), ...job, ...local } };
    setJobsById((prev) => ({ ...prev, [job.id]: { ...(prev[job.id] || {}), ...job, ...local } }));
  }, []);

  const patch = useCallback((id, local) => {
    if (jobsRef.current[id]) jobsRef.current = { ...jobsRef.current, [id]: { ...jobsRef.current[id], ...local } };
    setJobsById((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...local } } : prev));
  }, []);

  // When Toons is closed or refreshed in the middle of a build, the template is free at once for
  // the next time Toons is opened (no 90-second wait for the old lock).
  useEffect(() => {
    const free = () => building.current.forEach((id) => releaseLock(id));
    window.addEventListener("pagehide", free);
    window.addEventListener("beforeunload", free);
    return () => {
      window.removeEventListener("pagehide", free);
      window.removeEventListener("beforeunload", free);
    };
  }, []);

  // ---------------------------------------------------------------- settings

  const reloadSettings = useCallback(async () => {
    const res = await aiApi.settings(api);
    if (res.code === "not_deployed") setAvailable(false);
    else if (res.status) {
      setAvailable(true);
      setSettings(res);
    }
    return res;
  }, [api]);

  const saveSettings = useCallback(
    async (values) => {
      const res = await aiApi.saveSettings(api, values);
      if (res.status) setSettings(res);
      return res;
    },
    [api]
  );

  const removeKey = useCallback(async () => {
    const res = await aiApi.removeKey(api);
    if (res.status) setSettings(res);
    return res;
  }, [api]);

  // ---------------------------------------------------------------- finishing

  const refreshProjects = useCallback(async () => {
    try {
      const data = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
      const resp = await axios.get(api + "/api/Project/GetProject", {
        headers: { Authorization: "Bearer " + data.access_token },
      });
      if (resp.data && resp.data.result && setAllProject) setAllProject(resp.data.result);
    } catch (e) {
      /* My Projects reloads by itself next time */
    }
  }, [api, setAllProject]);

  const announce = useCallback(
    (job) => {
      if (!job || announced.current.has(job.id)) return;
      if (job.status !== "done" && job.status !== "failed") return;
      announced.current.add(job.id);
      if (job.batchId) {
        // A batch: one notification when its last template is finished (in this session).
        const others = Object.values(jobsRef.current).filter(
          (j) => j.batchId === job.batchId && j.id !== job.id && RUNNING.includes(j.status)
        );
        if (!others.length) {
          setToasts((prev) => prev.filter((t) => t.batchId !== job.batchId).concat({ jobId: job.id, batchId: job.batchId, kind: "batch" }));
        }
        if (!job.seen) aiApi.seen(api, job.id);
        return;
      }
      // Seen in the panel, or as a notification.
      if (panelRef.current !== job.id) {
        setToasts((prev) => prev.filter((t) => t.jobId !== job.id).concat({ jobId: job.id, kind: job.status }));
      }
      if (!job.seen) aiApi.seen(api, job.id);
    },
    [api]
  );

  // ---------------------------------------------------------------- building

  const build = useCallback(
    async (job) => {
      const id = job.id;
      if (building.current.has(id)) return;
      if (!takeLock(id)) {
        // Another tab builds it, or Toons was closed in the middle of it: try again later (the
        // old lock runs out after 90 seconds if that tab is gone).
        setTimeout(() => {
          const j = jobsRef.current[id];
          if (!j || building.current.has(id) || (j.status !== "planned" && j.status !== "building")) return;
          if (j.batchId) pumpRef.current && pumpRef.current();
          else buildRef.current && buildRef.current(j);
        }, RETRY_MS);
        return;
      }
      building.current.add(id);
      const plan = planOf(job);
      const images = [];
      try {
        if (!plan) throw new Error("The AI's plan could not be read.");
        const n = plan.scenes.length;
        merge(job, { status: "building", step: "design", previews: [], sceneCount: n, buildIndex: 0, progress: Math.max(job.progress || 0, 60) });
        await aiApi.building(api, id);

        // Designed for the template's size (standard 16:9, mobile 9:16 or square 1:1).
        const canvasSize = job.canvasSize || "16:9";
        const slides = await withCanvas(canvasSize, () =>
          buildSlides(plan, {
            apiBaseURL: api,
            withPresenter: job.presenter !== false,
            onScene: async (i, slide) => {
              refreshLock(id);
              try {
                images[i] = await renderSlide(slide, { apiBaseURL: api });
              } catch (e) {
                console.log("AI template: thumbnail could not be drawn.", e);
                images[i] = null;
              }
              patch(id, { previews: images.slice(), buildIndex: i + 1, progress: 60 + Math.round((30 * (i + 1)) / n) });
              await sleep(500); // each scene appears in the preview one after the other
            },
          })
        );

        patch(id, { step: "presenter", progress: 92 });
        await sleep(600);
        patch(id, { step: "save", progress: 95 });
        const projectData = packProjectData(slides);
        const res = await aiApi.complete(api, id, {
          projectName: plan.title,
          projectData,
          canvasSize,
          watermark: DEFAULT_WATERMARK,
          backgroundMusic: musicFor(plan),
          slideImages: images.map((x) => x || ""),
        });
        if (!res.status) throw new Error(res.message || "The template could not be saved.");
        const done = res.job || { ...job, status: "done", projectId: res.projectId };
        merge(done, { step: "done", progress: 100, previews: images.slice() });
        refreshProjects();
        reloadSettings();
        setHistoryVersion((v) => v + 1);
        announce({ ...done, seen: false });
      } catch (e) {
        const message = (e && e.message) || "The scenes could not be built.";
        console.log("AI template build failed", e);
        await aiApi.fail(api, id, message);
        merge({ id, status: "failed", error: message }, { step: "failed" });
        setHistoryVersion((v) => v + 1);
        announce({ ...job, status: "failed", error: message, seen: false });
      } finally {
        building.current.delete(id);
        releaseLock(id);
        // Batches are built one at a time: the next waiting template of a batch.
        if (job.batchId) setTimeout(() => pumpRef.current && pumpRef.current(), 300);
      }
    },
    [api, merge, patch, refreshProjects, reloadSettings, announce]
  );
  buildRef.current = build;

  /**
   * Builds the next batch template whose plan is ready, one at a time. Also used when Toons is
   * opened again: the API kept writing the plans while it was closed, and the rest are built here.
   */
  const pumpBatch = useCallback(() => {
    const busy = Array.from(building.current).some((id) => jobsRef.current[id] && jobsRef.current[id].batchId);
    if (busy) return;
    const waiting = Object.values(jobsRef.current)
      .filter((j) => j.batchId && (j.status === "planned" || j.status === "building") && planOf(j) && !building.current.has(j.id))
      .sort((a, b) => a.id - b.id);
    const next = waiting.find((j) => !lockedElsewhere(j.id));
    if (next) {
      build(next);
      return;
    }
    if (waiting.length && !pumpTimer.current) {
      pumpTimer.current = setTimeout(() => {
        pumpTimer.current = null;
        if (pumpRef.current) pumpRef.current();
      }, RETRY_MS);
    }
  }, [build]);
  pumpRef.current = pumpBatch;

  // ---------------------------------------------------------------- following jobs

  const handleServerJob = useCallback(
    (job) => {
      const local = jobsRef.current[job.id];
      // While this tab builds it, the browser's own state is newer than the server's.
      if (building.current.has(job.id)) return;
      if (local && local.status === "done" && job.status !== "done") return;
      merge(job);
      if (job.status === "planned" || job.status === "building") {
        // Batch templates are built one at a time (keeps the browser responsive).
        if (job.batchId) pumpBatch();
        else build(job);
      } else if (job.status === "done" || job.status === "failed") {
        if (!job.seen) announce(job);
      }
    },
    [merge, build, announce, pumpBatch]
  );

  // First look when the app opens: running generations, and ones that finished meanwhile.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      const s = await reloadSettings();
      if (cancelled || s.code === "not_deployed") return;
      const res = await aiApi.active(api);
      if (cancelled || !res.status) return;
      const list = res.jobs || [];
      // All of them first, so a batch is only called finished when none of its templates is left.
      list.forEach((j) => !jobsRef.current[j.id] && merge(j));
      list.forEach(handleServerJob);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, api, reloadSettings, handleServerJob, merge]);

  // Polling while the API is still working on something.
  const serverBusy = Object.values(jobsById).some((j) => SERVER_RUNNING.includes(j.status));
  useEffect(() => {
    if (!enabled || !serverBusy) return undefined;
    let stop = false;
    const tick = async () => {
      while (!stop) {
        await sleep(POLL_MS);
        if (stop) return;
        // Batch jobs are written by the API in order, a couple at a time: following the first
        // three of them is enough (not 30 requests every 2 seconds).
        const waiting = Object.values(jobsRef.current).filter((j) => SERVER_RUNNING.includes(j.status));
        const ids = waiting
          .filter((j) => !j.batchId)
          .concat(waiting.filter((j) => j.batchId).sort((a, b) => a.id - b.id).slice(0, 3))
          .map((j) => j.id);
        if (!ids.length) return;
        for (const id of ids) {
          const res = await aiApi.status(api, id);
          if (stop) return;
          if (res.status && res.job) handleServerJob(res.job);
        }
      }
    };
    tick();
    return () => {
      stop = true;
    };
  }, [enabled, serverBusy, api, handleServerJob]);

  // ---------------------------------------------------------------- actions

  const generate = useCallback(
    async ({ keyword, style, presenter, canvasSize }) => {
      const res = await aiApi.generate(api, { keyword, style, presenter, canvasSize });
      if (res.code === "not_deployed") setAvailable(false);
      if (!res.status || !res.job) return res;
      merge(res.job, { step: "queued", previews: [] });
      setPanelId(res.job.id);
      reloadSettings();
      setHistoryVersion((v) => v + 1);
      return res;
    },
    [api, merge, reloadSettings]
  );

  /** Admins: starts a batch ({ keywords[], style, presenter, categoryIds[], premium, name }). */
  const startBatch = useCallback(
    async (body) => {
      const res = await aiApi.batch(api, body);
      if (res.code === "not_deployed") setAvailable(false);
      if (!res.status || !res.batch) return res;
      (res.batch.jobs || []).forEach((x) => x && x.job && merge(x.job, { step: "queued", previews: [] }));
      return res;
    },
    [api, merge]
  );

  /** Admins: makes a topic again (same settings and batch). */
  const regenerate = useCallback(
    async (id) => {
      const res = await aiApi.regenerate(api, id);
      if (res.status && res.job) {
        merge(res.job, { step: "queued", previews: [] });
        // The batch is running again: its "finished" notification is out of date.
        if (res.job.batchId != null) setToasts((prev) => prev.filter((t) => t.batchId !== res.job.batchId));
      }
      setHistoryVersion((v) => v + 1);
      return res;
    },
    [api, merge]
  );

  /** Opens AI Studio on the batch tab (from a notification, on any page). */
  const openBatches = useCallback(() => {
    try {
      sessionStorage.setItem(BATCH_TAB_KEY, "batch");
    } catch (e) {
      /* ignore */
    }
    setToasts((prev) => prev.filter((t) => t.kind !== "batch"));
    if ((window.location.hash || "").toLowerCase().startsWith("#/editor")) history.push("/dashboard");
    if (setDivName) setDivName("AI Studio");
  }, [history, setDivName]);

  const cancel = useCallback(
    async (id) => {
      const res = await aiApi.cancel(api, id);
      if (res.status) merge({ id, status: "cancelled" });
      reloadSettings();
      setHistoryVersion((v) => v + 1);
      return res;
    },
    [api, merge, reloadSettings]
  );

  const openProject = useCallback(
    (job) => {
      if (!job || !job.projectId) return;
      const project = {
        id: job.projectId,
        projectName: job.projectName,
        canvasSize: job.canvasSize || "16:9",
        exportCount: 0,
        previewImagePath: job.previewImagePath,
      };
      sessionStorage.removeItem("createNewProject");
      sessionStorage.removeItem("CurrentChooseProjectId");
      sessionStorage.setItem("myProjectData", JSON.stringify(project));
      setPanelId(null);
      setToasts((prev) => prev.filter((t) => t.jobId !== job.id));
      if ((window.location.hash || "").toLowerCase().startsWith("#/editor")) window.location.reload();
      else history.push("/editor");
    },
    [history]
  );

  const openPanel = useCallback((id) => {
    setPanelId(id);
    setToasts((prev) => prev.filter((t) => t.jobId !== id));
  }, []);

  const dismissToast = useCallback((jobId) => setToasts((prev) => prev.filter((t) => t.jobId !== jobId)), []);

  const jobs = useMemo(() => Object.values(jobsById).sort((a, b) => b.id - a.id), [jobsById]);

  const value = {
    available,
    settings,
    jobs,
    jobsById,
    historyVersion,
    reloadSettings,
    saveSettings,
    removeKey,
    generate,
    startBatch,
    regenerate,
    openBatches,
    cancel,
    openProject,
    openPanel,
    closePanel: () => setPanelId(null),
  };

  return (
    <AIJobsContext.Provider value={value}>
      {children}
      {enabled && panelId != null && jobsById[panelId] && (
        <AIProgressPanel
          job={jobsById[panelId]}
          apiBaseURL={api}
          onClose={() => setPanelId(null)}
          onCancel={cancel}
          onOpen={openProject}
        />
      )}
      {enabled && (
        <AIToasts
          toasts={toasts}
          jobs={jobs}
          panelId={panelId}
          apiBaseURL={api}
          onDismiss={dismissToast}
          onOpenPanel={openPanel}
          onOpenProject={openProject}
          onOpenBatches={openBatches}
        />
      )}
    </AIJobsContext.Provider>
  );
}

export default AIJobsContext;
